import { useState, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

const EM_DASH = '—';

/** Hours to one decimal; em-dash for null/undefined (no budget on that side). */
const hrs = (value) =>
  value === null || value === undefined ? EM_DASH : Number(value).toFixed(1);

/**
 * "400.0 -> 480.0" for a column this upload changes; an em-dash otherwise.
 *
 * A column is unchanged when the CSV left the cell blank (detection fills the
 * omitted side with the current value) or supplied the value already stored,
 * and also when the package has no budget on that side at all. None of those is
 * what the reason is being attributed to, so repeating the value back as
 * "120.0 -> 120.0" would just be noise. Total Δ still reflects the real total.
 */
const beforeAfter = (oldVal, newVal) => {
  if (oldVal === newVal) return EM_DASH;
  return `${hrs(oldVal)} → ${hrs(newVal)}`;
};

/** Signed total delta, e.g. "+80.0" / "-40.0". */
const totalDelta = (change) => {
  const diff = (change.new_total_hrs || 0) - (change.old_total_hrs || 0);
  return `${diff >= 0 ? '+' : ''}${diff.toFixed(1)}`;
};

/**
 * BudgetChangeReasonsDialog (ticket #160 addendum)
 *
 * Shown between "Commit Changes" and the actual commit when a CSV update would
 * change budgeted hours. Collects ONE reason for the whole upload — it is
 * applied to every changed package — and lists the detected changes so the user
 * can see what the reason is being attributed to before anything is written.
 * Uploads whose changes have different reasons are meant to be split into
 * separate CSVs, which keeps this to three fields however many packages change.
 *
 * Display-only table: the rows come from the validation step's per-row
 * `budget_change` payload, already filtered by the caller to the rows it is
 * about to commit (a row the user set to SKIP must not appear here).
 *
 * Props:
 *   open       {boolean}  controls visibility
 *   changes    {Array}    detected budget changes about to be committed
 *   submitting {boolean}  commit in flight — disables both actions
 *   error      {string}   backend error, surfaced without closing the dialog
 *   onCancel   {function} abandon the upload (called only after confirmation)
 *   onApply    {function} ({ change_reason, change_order_reference, notes })
 */
export const BudgetChangeReasonsDialog = ({
  open,
  changes = [],
  submitting = false,
  error = null,
  onCancel,
  onApply,
}) => {
  const [changeReason, setChangeReason] = useState('');
  const [coReference, setCoReference] = useState('');
  const [notes, setNotes] = useState('');

  // Clear the form each time the dialog opens so a reason is never carried
  // over from a previous upload.
  useEffect(() => {
    if (open) {
      setChangeReason('');
      setCoReference('');
      setNotes('');
    }
  }, [open]);

  // Same rule the backend enforces (utils/budget-change-reason.js): a reason is
  // required, and a Change Order must carry its reference.
  const isReasonValid =
    changeReason !== '' &&
    (changeReason !== 'Change Order' || coReference.trim() !== '');

  const handleCancel = () => {
    if (submitting) return;
    if (window.confirm('Cancel this upload? No changes will be applied.')) {
      onCancel();
    }
  };

  const handleApply = (e) => {
    e.preventDefault();
    onApply({
      change_reason: changeReason,
      change_order_reference:
        changeReason === 'Change Order' ? coReference.trim() : null,
      notes: notes.trim() || null,
    });
  };

  return (
    <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
      <DialogTitle>Budget Changes Detected — Reason Required</DialogTitle>
      <form onSubmit={handleApply}>
        <DialogContent
          sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <Typography variant="body2" color="text.secondary">
            This upload changes budgeted hours on{' '}
            <strong>
              {changes.length} package{changes.length === 1 ? '' : 's'}
            </strong>
            . Enter the reason for these changes before the upload is applied.
            The reason applies to all changes below — upload separately if
            changes have different reasons.
          </Typography>

          <FormControl fullWidth required>
            <InputLabel id="csv-change-reason-label">Reason</InputLabel>
            <Select
              labelId="csv-change-reason-label"
              id="csv_change_reason"
              value={changeReason}
              label="Reason"
              onChange={(e) => setChangeReason(e.target.value)}
              disabled={submitting}
            >
              <MenuItem value="Original Budget">Original Budget</MenuItem>
              <MenuItem value="Change Order">Change Order</MenuItem>
              <MenuItem value="Budget Correction">Budget Correction</MenuItem>
              <MenuItem value="Transfer Between Packages">
                Transfer Between Packages
              </MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          {changeReason === 'Change Order' && (
            <TextField
              required
              fullWidth
              id="csv_co_reference"
              label="CO Reference"
              type="text"
              value={coReference}
              onChange={(e) => setCoReference(e.target.value)}
              disabled={submitting}
            />
          )}

          {changeReason === 'Transfer Between Packages' && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Remember to make the matching change on the other package.
            </Alert>
          )}

          <TextField
            fullWidth
            id="csv_budget_notes"
            label="Notes"
            type="text"
            multiline
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={submitting}
          />

          <Divider />

          {/* Display-only: what the reason above is being attributed to. */}
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 260 }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Package</TableCell>
                  <TableCell align="right">Field (old &rarr; new)</TableCell>
                  <TableCell align="right">Shop (old &rarr; new)</TableCell>
                  <TableCell align="right">Total &Delta;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {changes.map((c) => (
                  <TableRow key={c.package_id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {c.package_name || c.package_id}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {beforeAfter(c.old_field_hrs, c.new_field_hrs)}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {beforeAfter(c.old_shop_hrs, c.new_shop_hrs)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}
                    >
                      {totalDelta(c)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={submitting}>
            Cancel Upload
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isReasonValid || submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : undefined}
          >
            Apply Upload
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default BudgetChangeReasonsDialog;
